/**
 * Design-system primitives, metric-for-metric from the handoff.
 *
 * Rules baked in here so no screen has to remember them:
 *  - cards are `surf` on a 1px `line` border, radius 16–18, no shadows
 *  - `dim` is valid only on `bg`/`surf`; anything on a `fill` ground uses `ink`
 *  - every tap target is at least 44px
 *  - chips are mono; body is Archivo
 */
import {
  forwardRef, useEffect, useId, useRef, useState,
  type ButtonHTMLAttributes, type CSSProperties, type InputHTMLAttributes, type ReactNode,
} from 'react'
import { Link } from 'react-router-dom'
import { TEAMS_BY_ID, teamAbbr, teamLogoUrl } from '@shared/teams'
import { useTheme } from '../theme/ThemeProvider'
import { Wordmark } from './Wordmark'

// ── layout ──────────────────────────────────────────────────────────────────

/**
 * A team's logo, with the abbreviation as both the alt text and the fallback.
 *
 * ESPN's logo CDN is a third party we do not control, and a broken-image icon
 * in a header is worse than three letters, so a failed load swaps to the
 * abbreviation rather than leaving a hole.
 *
 * `fallback="none"` collapses to nothing instead, for the places where the
 * abbreviation is already printed right next to the badge. Those read
 * "PIT PIT vs ATL ATL" the moment the CDN is unreachable, which is a worse
 * failure than the missing logo it is standing in for.
 */
export function TeamLogo({
  teamId, size = 48, fallback = 'abbr', style,
}: {
  teamId: string | null | undefined
  size?: number
  fallback?: 'abbr' | 'none'
  style?: CSSProperties
}) {
  const [broken, setBroken] = useState(false)
  const src = teamLogoUrl(teamId)
  const abbr = teamId ? teamAbbr(teamId) : ''
  const name = teamId && TEAMS_BY_ID[teamId]
    ? `${TEAMS_BY_ID[teamId].location} ${TEAMS_BY_ID[teamId].nickname}`
    : abbr

  const box: CSSProperties = {
    width: size, height: size, flex: '0 0 auto',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    ...style,
  }

  if (!src || broken) {
    if (fallback === 'none') return null
    return (
      <div className="mono" style={{ ...box, fontSize: Math.round(size * 0.28), fontWeight: 700 }} aria-label={name}>
        {abbr}
      </div>
    )
  }
  return (
    <img
      src={src}
      alt={name}
      width={size}
      height={size}
      onError={() => setBroken(true)}
      style={{ ...box, objectFit: 'contain' }}
    />
  )
}

/**
 * True above the desktop breakpoint — the same 1025px the stylesheet uses.
 *
 * A hook rather than a media query because two things that need it can't be
 * expressed in CSS: the nav decides how many items to *render*, and the fund
 * sheet swaps a QR for a deep link. Both are structure, not styling.
 */
export function useIsDesktop(): boolean {
  const [wide, setWide] = useState(
    () => typeof window !== 'undefined' && window.matchMedia('(min-width: 1025px)').matches,
  )
  useEffect(() => {
    if (typeof window === 'undefined') return
    const mq = window.matchMedia('(min-width: 1025px)')
    const on = () => setWide(mq.matches)
    mq.addEventListener('change', on)
    return () => mq.removeEventListener('change', on)
  }, [])
  return wide
}

export function Screen({ children, style }: { children: ReactNode; style?: CSSProperties }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', minHeight: '100dvh', background: 'var(--bg)', ...style }}>
      {children}
    </div>
  )
}

/**
 * `logo` puts the viewer's team badge at the far left, ahead of `left`.
 *
 * Default on, because that is every screen a person lands on. Off for the
 * headers whose `left` is a back button: two things competing for the top-left
 * corner is one too many, and on those screens the back affordance has to win.
 *
 * `wordmark` puts the Sunday Football logo in the bar, between the team badge
 * and whatever sits on the right. It is a flex child of that row rather than
 * an absolutely-positioned layer or a line of its own: the badge and the
 * avatar take their widths first, the mark takes the space that is left and
 * centres itself in it, and nothing can collide because nothing overlaps.
 *
 * That is also why it has no fixed size. The row is the constraint, so the
 * mark is exactly as large as the gap allows on whatever screen it lands on.
 *
 * It defaults on for a bar carrying no title and no left slot. A screen that
 * names itself keeps its name, and the filled game-day bar is never a
 * candidate; Home passes it explicitly.
 *
 * `title` is centred in the row rather than sitting next to the badge, and it
 * is centred on the ROW, not on what is left over after the badge — so a back
 * button on one side and an action on the other do not drag it off-centre.
 * `pointer-events: none` keeps it from eating taps meant for either.
 */
export function Header({
  title, left, right, sub, bordered = true, filled = false, logo = true, wordmark, style,
}: {
  title?: ReactNode; left?: ReactNode; right?: ReactNode; sub?: ReactNode
  bordered?: boolean; filled?: boolean; logo?: boolean; wordmark?: boolean
  style?: CSSProperties
}) {
  const { teamId } = useTheme()
  const showWordmark = wordmark ?? (!title && !left)
  return (
    <div
      style={{
        flex: '0 0 auto',
        padding: filled ? '16px 18px 22px' : '16px 18px 12px',
        paddingTop: `calc(16px + env(safe-area-inset-top, 0px))`,
        borderBottom: bordered && !filled ? '1px solid var(--line)' : undefined,
        background: filled ? 'var(--fill)' : 'var(--bg)',
        color: filled ? 'var(--ink)' : 'var(--fg)',
        ...style,
      }}
    >
      <div className="sfg-shell">
      <div
        style={{
          position: 'relative',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12,
          minHeight: 48,
        }}
      >
        {title ? (
          <div
            style={{
              position: 'absolute', inset: 0, display: 'flex',
              alignItems: 'center', justifyContent: 'center', pointerEvents: 'none',
            }}
          >
            <div className="screen-title" style={{ minWidth: 0, textAlign: 'center' }}>{title}</div>
          </div>
        ) : null}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0, flex: '0 0 auto' }}>
          {logo ? (
            // The badge is a link home. It is the most prominent thing in the
            // corner of every screen, it looks exactly like a logo, and every
            // app anybody has ever used treats that as the way back.
            <Link to="/" aria-label="Home" style={{ display: 'flex', marginLeft: -4 }}>
              <TeamLogo teamId={teamId} size={48} />
            </Link>
          ) : null}
          {left}
        </div>
        {showWordmark ? (
          <div style={{ flex: '1 1 auto', minWidth: 0, display: 'flex', padding: '0 20px' }}>
            {/*
              currentColor: --ink on a filled bar, --fg everywhere else.

              The height cap is what stops the mark being as tall as the space
              is wide. The art is 399 × 30, so on a phone the width runs out
              first and the mark lands at ~17px — the cap never bites. On a
              desktop window the width does not run out, and without a ceiling
              the same rule produced a 96px-tall banner over a 48px badge. At
              34px it stays the badge's junior partner, and the SVG's own
              preserveAspectRatio centres it in what is left.
            */}
            <Wordmark
              width="100%"
              style={{ color: filled ? 'var(--ink)' : 'var(--fg)', maxHeight: 34 }}
            />
          </div>
        ) : null}
        {right}
      </div>
      {sub ? <div style={{ marginTop: 6 }}>{sub}</div> : null}
      </div>
    </div>
  )
}

export function Body({ children, style, pad = true }: { children: ReactNode; style?: CSSProperties; pad?: boolean }) {
  return (
    <div
      // `sfg-shell` is inert below the desktop breakpoint; above it, this is
      // the column the header lines itself up with.
      className="scr sfg-shell"
      style={{
        flex: '1 1 auto',
        padding: pad ? '16px var(--gutter) calc(24px + var(--nav-h))' : undefined,
        ...style,
      }}
    >
      {children}
    </div>
  )
}

export function Card({
  children, style, dashed = false, accent = false, onClick,
}: {
  children: ReactNode; style?: CSSProperties; dashed?: boolean; accent?: boolean
  onClick?: () => void
}) {
  const Tag = onClick ? 'button' : 'div'
  return (
    <Tag
      onClick={onClick}
      style={{
        display: 'block',
        width: '100%',
        textAlign: 'left',
        background: 'var(--surf)',
        border: `1px ${dashed ? 'dashed' : 'solid'} ${accent ? 'var(--fill)' : 'var(--line)'}`,
        borderRadius: 'var(--radius-tile)',
        padding: 16,
        color: 'var(--fg)',
        ...style,
      }}
    >
      {children}
    </Tag>
  )
}

export function Eyebrow({ children, large = false, style }: { children: ReactNode; large?: boolean; style?: CSSProperties }) {
  return <div className={large ? 'eyebrow-lg' : 'eyebrow'} style={style}>{children}</div>
}

export function Divider({ style }: { style?: CSSProperties }) {
  return <div style={{ height: 1, background: 'var(--line)', ...style }} />
}

export function Row({
  left, right, onClick, style, borderTop = false, borderBottom = true, padding = '12px 0',
}: {
  left: ReactNode; right?: ReactNode; onClick?: () => void; style?: CSSProperties
  borderTop?: boolean; borderBottom?: boolean; padding?: string
}) {
  const Tag = onClick ? 'button' : 'div'
  return (
    <Tag
      onClick={onClick}
      style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12,
        width: '100%', textAlign: 'left', color: 'var(--fg)',
        padding,
        borderTop: borderTop ? '1px solid var(--line)' : undefined,
        borderBottom: borderBottom ? '1px solid var(--line)' : undefined,
        minHeight: onClick ? 44 : undefined,
        ...style,
      }}
    >
      <span style={{ minWidth: 0 }}>{left}</span>
      {right !== undefined ? <span style={{ flex: '0 0 auto', color: 'var(--dim)' }}>{right}</span> : null}
    </Tag>
  )
}

// ── buttons ─────────────────────────────────────────────────────────────────

type Variant = 'primary' | 'secondary' | 'tertiary' | 'outline-bright'

const BTN_BASE: CSSProperties = {
  display: 'block', width: '100%', textAlign: 'center',
  borderRadius: 'var(--radius-btn)', fontWeight: 700, fontSize: 15,
  padding: '15px 16px', minHeight: 48,
}

function variantStyle(v: Variant, disabled?: boolean): CSSProperties {
  if (disabled) return { background: 'var(--line)', color: 'var(--dim)' }
  switch (v) {
    case 'primary': return { background: 'var(--fill)', color: 'var(--ink)' }
    case 'secondary': return { background: 'transparent', color: 'var(--fg)', border: '1px solid var(--outline)', fontWeight: 600 }
    case 'tertiary': return { background: 'transparent', color: 'var(--dim)', fontWeight: 600 }
    case 'outline-bright': return { background: 'transparent', color: 'var(--bright)', border: '1.5px solid var(--bright)' }
  }
}

interface BtnProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant
  compact?: boolean
}

export const Button = forwardRef<HTMLButtonElement, BtnProps>(function Button(
  { variant = 'primary', compact = false, style, disabled, children, ...rest }, ref,
) {
  return (
    <button
      ref={ref}
      disabled={disabled}
      style={{
        ...BTN_BASE,
        ...(compact ? { padding: '12px 14px', fontSize: 14, minHeight: 44, borderRadius: 12 } : null),
        ...variantStyle(variant, disabled),
        ...style,
      }}
      {...rest}
    >
      {children}
    </button>
  )
})

/** Confirm / Dispute, Accept / Decline — two flexed buttons, 8px gap. */
export function ButtonPair({
  primaryLabel, secondaryLabel, onPrimary, onSecondary, disabled, busy,
}: {
  primaryLabel: string; secondaryLabel: string
  onPrimary: () => void; onSecondary: () => void
  disabled?: boolean; busy?: boolean
}) {
  return (
    <div style={{ display: 'flex', gap: 8, marginTop: 14 }}>
      <button
        onClick={onPrimary}
        disabled={disabled || busy}
        style={{
          flex: 1, textAlign: 'center', padding: 10, borderRadius: 10, minHeight: 44,
          background: 'var(--fill)', color: 'var(--ink)', fontSize: 13, fontWeight: 600,
          opacity: disabled || busy ? 0.6 : 1,
        }}
      >
        {busy ? '…' : primaryLabel}
      </button>
      <button
        onClick={onSecondary}
        disabled={disabled || busy}
        style={{
          flex: 1, textAlign: 'center', padding: 10, borderRadius: 10, minHeight: 44,
          border: '1px solid var(--outline)', color: 'var(--fg)', fontSize: 13, fontWeight: 600,
          opacity: disabled || busy ? 0.6 : 1,
        }}
      >
        {secondaryLabel}
      </button>
    </div>
  )
}

export function Pill({
  children, selected, onClick, style, dashed,
}: {
  children: ReactNode; selected?: boolean; onClick?: () => void; style?: CSSProperties; dashed?: boolean
}) {
  return (
    <button
      onClick={onClick}
      style={{
        fontFamily: 'var(--font-mono)', fontSize: 11, fontWeight: 600,
        padding: '6px 12px', borderRadius: 999, minHeight: 32,
        background: selected ? 'var(--fill)' : 'transparent',
        color: selected ? 'var(--ink)' : 'var(--dim)',
        border: selected ? '1px solid var(--fill)' : `1px ${dashed ? 'dashed' : 'solid'} var(--outline)`,
        ...style,
      }}
    >
      {children}
    </button>
  )
}

/** A two-option segmented control — the attendance tri-state, visibility, etc. */
export function Segmented<T extends string>({
  options, value, onChange, size = 'md',
}: {
  options: { value: T; label: string }[]
  value: T | null
  onChange: (v: T) => void
  size?: 'sm' | 'md'
}) {
  return (
    <div style={{ display: 'flex', gap: size === 'sm' ? 8 : 10 }}>
      {options.map((o) => {
        const on = value === o.value
        return (
          <button
            key={o.value}
            onClick={() => onChange(o.value)}
            aria-pressed={on}
            style={{
              flex: 1, textAlign: 'center',
              padding: size === 'sm' ? 11 : 14,
              minHeight: 44,
              borderRadius: size === 'sm' ? 10 : 12,
              fontSize: size === 'sm' ? 13 : 14,
              fontWeight: on ? 700 : 600,
              background: on ? 'var(--fill)' : 'transparent',
              color: on ? 'var(--ink)' : 'var(--dim)',
              border: on ? '1px solid var(--fill)' : '1px solid var(--outline)',
            }}
          >
            {o.label}
          </button>
        )
      })}
    </div>
  )
}

export function Stepper({
  value, onChange, min = 0, max = 99, step = 1, format,
}: {
  value: number; onChange: (n: number) => void
  min?: number; max?: number; step?: number
  format?: (n: number) => string
}) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 14, border: '1px solid var(--outline)', borderRadius: 999, padding: '6px 14px' }}>
      <button
        aria-label="Decrease"
        onClick={() => onChange(Math.max(min, value - step))}
        style={{ fontSize: 18, color: 'var(--dim)', minWidth: 32, minHeight: 32 }}
      >−</button>
      <span className="mono" style={{ fontSize: 14, minWidth: 24, textAlign: 'center' }}>
        {format ? format(value) : value}
      </span>
      <button
        aria-label="Increase"
        onClick={() => onChange(Math.min(max, value + step))}
        style={{ fontSize: 18, color: 'var(--bright)', minWidth: 32, minHeight: 32 }}
      >+</button>
    </div>
  )
}

export function BigStepper({
  value, onChange, min, max, step, format,
}: {
  value: number; onChange: (n: number) => void
  min: number; max: number; step: number; format: (n: number) => string
}) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginTop: 18 }}>
      <button
        aria-label="Decrease"
        onClick={() => onChange(Math.max(min, value - step))}
        style={{ width: 44, height: 44, borderRadius: 12, border: '1px solid var(--outline)', fontSize: 18, color: 'var(--fg)' }}
      >−</button>
      <div className="numerals" style={{ flex: 1, textAlign: 'center', fontSize: 30 }}>{format(value)}</div>
      <button
        aria-label="Increase"
        onClick={() => onChange(Math.min(max, value + step))}
        style={{ width: 44, height: 44, borderRadius: 12, border: '1px solid var(--outline)', fontSize: 18, color: 'var(--bright)' }}
      >+</button>
    </div>
  )
}

export function Toggle({ on, onChange, label }: { on: boolean; onChange: (v: boolean) => void; label?: string }) {
  return (
    <button
      role="switch"
      aria-checked={on}
      aria-label={label}
      onClick={() => onChange(!on)}
      style={{
        width: 40, height: 23, borderRadius: 999, position: 'relative', flex: '0 0 auto',
        background: on ? 'var(--fill)' : 'transparent',
        border: on ? '1px solid var(--fill)' : '1px solid var(--outline)',
      }}
    >
      <span
        style={{
          position: 'absolute', top: 2, [on ? 'right' : 'left']: 2,
          width: 17, height: 17, borderRadius: 999,
          background: on ? 'var(--ink)' : 'var(--line)',
          transition: 'all 140ms ease',
        } as CSSProperties}
      />
    </button>
  )
}

// ── inputs ──────────────────────────────────────────────────────────────────

interface FieldProps extends InputHTMLAttributes<HTMLInputElement> {
  label?: string
  hint?: ReactNode
  error?: string | null
  inSheet?: boolean
  shake?: boolean
}

export const Field = forwardRef<HTMLInputElement, FieldProps>(function Field(
  { label, hint, error, inSheet, shake, style, ...rest }, ref,
) {
  /*
   * A real <label for>, not a div that happens to sit above the input.
   *
   * It was a div, which meant every field in the app had no accessible name:
   * a screen reader announced "edit text" and tapping the caption didn't focus
   * anything. `useId` so two fields on one screen can't collide, and a caller
   * that supplies its own `id` keeps it.
   */
  const autoId = useId()
  const id = rest.id ?? autoId

  return (
    <div>
      {label ? (
        <label
          htmlFor={id}
          style={{ display: 'block', fontSize: 12, color: 'var(--dim)', marginBottom: 6 }}
        >
          {label}
        </label>
      ) : null}
      <input
        ref={ref}
        id={id}
        className={shake ? 'shake' : undefined}
        style={{
          width: '100%', padding: '15px 16px', borderRadius: 'var(--radius-btn)',
          border: `1px solid ${error ? 'var(--alert-line)' : 'var(--line)'}`,
          background: inSheet ? 'var(--bg)' : 'var(--surf)',
          fontSize: 15, color: 'var(--fg)', minHeight: 48,
          ...style,
        }}
        {...rest}
      />
      {error ? <div style={{ fontSize: 12, color: 'var(--alert-fg)', marginTop: 6 }}>{error}</div> : null}
      {!error && hint ? <div style={{ fontSize: 12, color: 'var(--dim)', marginTop: 8 }}>{hint}</div> : null}
    </div>
  )
})

export function ProgressBar({ pct, height = 8, style }: { pct: number; height?: number; style?: CSSProperties }) {
  const clamped = Math.max(0, Math.min(100, pct))
  return (
    <div
      role="progressbar"
      aria-valuenow={Math.round(clamped)}
      aria-valuemin={0}
      aria-valuemax={100}
      style={{ height, borderRadius: height / 2, background: 'var(--line)', overflow: 'hidden', ...style }}
    >
      <div style={{ height: '100%', width: `${clamped}%`, background: 'var(--fill)', transition: 'width 300ms ease' }} />
    </div>
  )
}

export function Avatar({
  initials, size = 44, self = false, style,
}: { initials: string; size?: number; self?: boolean; style?: CSSProperties }) {
  return (
    <div
      style={{
        width: size, height: size, borderRadius: 999, flex: '0 0 auto',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: size >= 70 ? 24 : 13, fontWeight: size >= 70 ? 800 : 700,
        background: self ? 'var(--fill)' : 'var(--surf)',
        color: self ? 'var(--ink)' : 'var(--fg)',
        border: self ? 'none' : '1px solid var(--line)',
        ...style,
      }}
    >
      {initials}
    </div>
  )
}

// ── sheet ───────────────────────────────────────────────────────────────────

export function Sheet({
  open, onClose, children, labelledBy,
}: { open: boolean; onClose: () => void; children: ReactNode; labelledBy?: string }) {
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    document.addEventListener('keydown', onKey)
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    ref.current?.focus()
    return () => {
      document.removeEventListener('keydown', onKey)
      document.body.style.overflow = prev
    }
  }, [open, onClose])

  if (!open) return null

  return (
    <div
      style={{ position: 'fixed', inset: 0, zIndex: 50, display: 'flex', flexDirection: 'column', justifyContent: 'flex-end' }}
      role="dialog"
      aria-modal="true"
      aria-labelledby={labelledBy}
    >
      <button
        aria-label="Close"
        onClick={onClose}
        style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,.55)', animation: 'sfg-fade-in 160ms ease', cursor: 'default' }}
      />
      <div
        ref={ref}
        tabIndex={-1}
        className="scr"
        style={{
          position: 'relative',
          background: 'var(--surf)',
          borderTop: '1px solid var(--line)',
          borderRadius: 'var(--radius-sheet) var(--radius-sheet) 0 0',
          padding: `20px 18px calc(28px + env(safe-area-inset-bottom, 0px))`,
          maxHeight: '88dvh',
          animation: 'sfg-sheet-in 240ms cubic-bezier(.2,.8,.2,1)',
        }}
      >
        <div style={{ width: 40, height: 4, borderRadius: 2, background: 'var(--line)', margin: '0 auto 18px' }} />
        {children}
      </div>
    </div>
  )
}

// ── state displays ──────────────────────────────────────────────────────────

export function EmptyState({
  title, detail, actionLabel, onAction, variant = 'primary',
}: {
  title: string; detail?: string; actionLabel?: string; onAction?: () => void
  variant?: 'primary' | 'secondary'
}) {
  return (
    <Card style={{ padding: '26px 18px', textAlign: 'center' }}>
      <div style={{ fontSize: 15, fontWeight: 600 }}>{title}</div>
      {detail ? <div style={{ fontSize: 13, color: 'var(--dim)', marginTop: 6 }}>{detail}</div> : null}
      {actionLabel && onAction ? (
        <button
          onClick={onAction}
          style={{
            marginTop: 14, display: 'inline-block', padding: '11px 20px', borderRadius: 999,
            fontSize: 13, fontWeight: 700, minHeight: 44,
            background: variant === 'primary' ? 'var(--fill)' : 'transparent',
            color: variant === 'primary' ? 'var(--ink)' : 'var(--bright)',
            border: variant === 'primary' ? 'none' : '1px solid var(--outline)',
          }}
        >
          {actionLabel}
        </button>
      ) : null}
    </Card>
  )
}

/** No spinners anywhere — a stale score always beats no score (handoff, 3x). */
export function SkeletonCard({ lines = 3 }: { lines?: number }) {
  const widths = ['60%', '85%', '40%', '70%']
  return (
    <Card>
      {Array.from({ length: lines }, (_, i) => (
        <div key={i} className="skeleton-bar" style={{ width: widths[i % widths.length], marginTop: i ? 10 : 0 }} />
      ))}
    </Card>
  )
}

export function Notice({
  tone, children, style,
}: { tone: 'warn' | 'alert' | 'info'; children: ReactNode; style?: CSSProperties }) {
  const colors = tone === 'warn'
    ? { color: 'var(--warn-fg)', border: '1px solid var(--warn-line)' }
    : tone === 'alert'
      ? { color: 'var(--alert-fg)', border: '1px solid var(--alert-line)' }
      : { color: 'var(--dim)', border: '1px solid var(--line)' }
  return (
    <div
      className="mono"
      role="status"
      style={{ padding: '12px 14px', borderRadius: 12, fontSize: 12, ...colors, ...style }}
    >
      {children}
    </div>
  )
}

export function Toast({ message, action, onAction, onDismiss }: {
  message: string; action?: string; onAction?: () => void; onDismiss?: () => void
}) {
  useEffect(() => {
    if (!onDismiss) return
    const t = setTimeout(onDismiss, action ? 8000 : 4000)
    return () => clearTimeout(t)
  }, [onDismiss, action])
  return (
    <div
      role="status"
      style={{
        position: 'fixed', left: 12, right: 12, bottom: `calc(12px + var(--nav-h))`, zIndex: 60,
        background: 'var(--surf)', border: '1px solid var(--line)', borderRadius: 14,
        padding: '12px 14px', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        gap: 12, fontSize: 14, animation: 'sfg-fade-in 160ms ease', maxWidth: 520, margin: '0 auto',
      }}
    >
      <span style={{ minWidth: 0 }}>{message}</span>
      {action && onAction ? (
        <button onClick={onAction} style={{ color: 'var(--bright)', fontWeight: 700, fontSize: 13, flex: '0 0 auto', minHeight: 32 }}>
          {action}
        </button>
      ) : null}
    </div>
  )
}

/**
 * Freshness label on any live surface: normal → amber >2m → red >10m (D36).
 *
 * `onFill` is not cosmetic. `dim` and the two alert hues are only legible on
 * `bg`/`surf`; on a `fill` ground they fail badly — the handoff measured 1.33:1
 * for `dim` on the Steelers gold. On fill we derive everything from `ink`
 * instead, and lean on weight rather than hue to carry the warning.
 */
export function Freshness({
  asOf, health, onFill = false,
}: {
  asOf: number | null
  health: 'ok' | 'stale' | 'paused' | 'unavailable'
  onFill?: boolean
}) {
  const [, tick] = useState(0)
  useEffect(() => {
    const t = setInterval(() => tick((n) => n + 1), 15_000)
    return () => clearInterval(t)
  }, [])

  if (health === 'unavailable' || !asOf) return null

  const age = Date.now() - asOf
  const label = age < 60_000
    ? `updated ${Math.max(1, Math.round(age / 1000))}s ago`
    : `updated ${Math.round(age / 60_000)}m ago`
  const text = health === 'paused' ? 'scores paused, reconnecting'
    : health === 'stale' ? `may be stale, ${label}`
      : label

  const style: CSSProperties = onFill
    ? {
        fontSize: 10,
        color: 'var(--ink)',
        opacity: health === 'ok' ? 0.75 : 1,
        fontWeight: health === 'ok' ? 400 : 600,
      }
    : {
        fontSize: 10,
        color: health === 'paused' ? 'var(--alert-fg)' : health === 'stale' ? 'var(--warn-fg)' : 'var(--dim)',
      }

  return <span className="mono" style={style}>{text}</span>
}

export function Spacer({ h }: { h: number }) { return <div style={{ height: h }} /> }
